import React, { useEffect, useMemo, useState } from 'react'
import { portal } from '../portalApi.js'
import RichTextEditor from '../components/RichTextEditor.jsx'

// 업무매뉴얼 — 노션에서 옮겨온 뒤로는 여기가 정본이다.
//
// 왼쪽은 파트 > 대분류 > 소분류로 접어 둔 목록, 오른쪽이 본문. 노션의 분류를 그대로
// 가져온 이유는 쓰던 사람들이 찾던 자리에서 그대로 찾게 하려는 것이다 - 옮기면서
// 분류까지 바꾸면 "노션에 있던 그거 어디 갔냐"가 된다.

const 미분류 = '기타'

function group(docs) {
  const tree = new Map()
  for (const d of docs) {
    const p = d.part || 미분류
    const c = d.category || 미분류
    if (!tree.has(p)) tree.set(p, new Map())
    if (!tree.get(p).has(c)) tree.get(p).set(c, [])
    tree.get(p).get(c).push(d)
  }
  return tree
}

function DocList({ docs, activeId, onPick }) {
  const tree = useMemo(() => group(docs), [docs])
  if (!docs.length) return <div className="muted" style={{ padding: 12 }}>문서가 없습니다</div>
  return (
    <div className="mn-list">
      {[...tree.entries()].map(([part, cats]) => (
        <div key={part} className="mn-part">
          <div className="mn-part-title">{part}</div>
          {[...cats.entries()].map(([cat, items]) => (
            <div key={cat} className="mn-cat">
              <div className="mn-cat-title">{cat}</div>
              {items.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`mn-item${d.id === activeId ? ' on' : ''}`}
                  onClick={() => onPick(d.id)}
                >
                  <span className="mn-item-name">{d.title}</span>
                  {!d.published && <span className="mn-badge">비공개</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

const 빈문서 = { title: '', part: '', category: '', subcategory: '', owner_name: '', screen_key: '', published: 0, body_html: '' }

export default function Manual() {
  const [docs, setDocs] = useState([])
  const [canEdit, setCanEdit] = useState(false)
  const [screenOptions, setScreenOptions] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [doc, setDoc] = useState(null)
  const [draft, setDraft] = useState(null)   // null 이면 읽기 모드
  const [q, setQ] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [history, setHistory] = useState(null)

  const loadList = () => portal.get('/api/manual/docs')
    .then((r) => { setDocs(r.docs || []); setCanEdit(!!r.canEdit) })
    .catch((e) => setErr(e.message))

  useEffect(() => { loadList() }, [])

  // 편집자에게만 열리는 목록이라, 권한이 확인된 뒤에 부른다 (아니면 매번 403 이 찍힌다)
  useEffect(() => {
    if (!canEdit) return
    portal.get('/api/manual/screens')
      .then((r) => setScreenOptions(r.screens || []))
      .catch(() => {})
  }, [canEdit])

  useEffect(() => {
    if (!activeId) { setDoc(null); return }
    setDoc(null)
    setHistory(null)
    portal.get(`/api/manual/docs/${activeId}`).then(setDoc).catch((e) => setErr(e.message))
  }, [activeId])

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase()
    if (!t) return docs
    return docs.filter((d) => [d.title, d.part, d.category, d.subcategory, d.owner_name]
      .filter(Boolean).some((v) => v.toLowerCase().includes(t)))
  }, [docs, q])

  // 붙여넣은 캡처는 본문에 base64로 넣지 않고 올린 뒤 주소만 남긴다 (manual.py 주석 참고)
  const uploadImage = async (dataUrl) => {
    const r = await portal.post('/api/manual/assets', { dataUrl })
    return r.url
  }

  const startNew = () => { setActiveId(null); setDoc(null); setDraft({ ...빈문서 }) }
  const startEdit = () => setDraft({ ...doc })

  const save = async () => {
    if (!draft.title.trim()) { setErr('제목을 입력해 주세요'); return }
    setBusy(true)
    setErr('')
    try {
      const payload = {
        title: draft.title, part: draft.part, category: draft.category,
        subcategory: draft.subcategory, owner_name: draft.owner_name,
        screen_key: draft.screen_key || '', published: draft.published ? 1 : 0,
        body_html: draft.body_html || '',
      }
      const saved = draft.id
        ? await portal.put(`/api/manual/docs/${draft.id}`, payload)
        : await portal.post('/api/manual/docs', payload)
      setDraft(null)
      setDoc(saved)
      setActiveId(saved.id)
      await loadList()
    } catch (e) { setErr(e.message) } finally { setBusy(false) }
  }

  const archive = async () => {
    if (!window.confirm(`「${doc.title}」을(를) 목록에서 내립니다. 계속할까요?`)) return
    try {
      await portal.post(`/api/manual/docs/${doc.id}/archive`)
      setActiveId(null)
      await loadList()
    } catch (e) { setErr(e.message) }
  }

  const showHistory = async () => {
    try {
      const r = await portal.get(`/api/manual/docs/${doc.id}/revisions`)
      setHistory(r.revisions || [])
    } catch (e) { setErr(e.message) }
  }

  return (
    <>
      <h2>업무매뉴얼</h2>
      {err && <div className="error-box" onClick={() => setErr('')}>{err}</div>}

      <div className="mn-wrap">
        <div className="panel mn-side">
          <input className="mn-search" placeholder="제목·담당자 검색"
            value={q} onChange={(e) => setQ(e.target.value)} />
          {canEdit && (
            <button type="button" className="btn tiny" style={{ margin: '0 8px 8px' }}
              onClick={startNew}>+ 새 문서</button>
          )}
          <DocList docs={shown} activeId={activeId} onPick={(id) => { setDraft(null); setActiveId(id) }} />
        </div>

        <div className="panel mn-main">
          {draft && (
            <div className="mn-edit">
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input className="mn-title-input" placeholder="제목" value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
              </div>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <input placeholder="파트 (예: 회계)" value={draft.part || ''}
                  onChange={(e) => setDraft({ ...draft, part: e.target.value })} />
                <input placeholder="대분류 (예: 자금)" value={draft.category || ''}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
                <input placeholder="소분류 (예: 정기)" value={draft.subcategory || ''}
                  onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} />
                <input placeholder="담당자" value={draft.owner_name || ''}
                  onChange={(e) => setDraft({ ...draft, owner_name: e.target.value })} />
              </div>
              <div className="row" style={{ gap: 12, marginTop: 8, alignItems: 'center' }}>
                <label className="muted" style={{ fontSize: 12 }}>연결 화면</label>
                <select value={draft.screen_key || ''}
                  onChange={(e) => setDraft({ ...draft, screen_key: e.target.value })}>
                  <option value="">— 연결 안 함 (매뉴얼 권한자 전체 공개) —</option>
                  {screenOptions.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <label style={{ fontSize: 12 }}>
                  <input type="checkbox" checked={!!draft.published}
                    onChange={(e) => setDraft({ ...draft, published: e.target.checked ? 1 : 0 })} />
                  {' '}공개
                </label>
              </div>
              <div className="hint" style={{ marginTop: 6 }}>
                연결 화면을 고르면 <b>그 화면 권한이 있는 사람만</b> 이 문서를 봅니다.
                계좌번호·인증 절차가 적힌 문서는 반드시 연결해 주세요.
              </div>
              <div style={{ marginTop: 10 }}>
                <RichTextEditor
                  value={draft.body_html || ''}
                  onChange={(v) => setDraft({ ...draft, body_html: v })}
                  uploadImage={uploadImage}
                  placeholder="본문 — 캡처는 Ctrl+V 로 바로 붙여넣을 수 있습니다"
                  minHeight={360}
                />
              </div>
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <button type="button" className="btn" disabled={busy} onClick={save}>
                  {busy ? '저장 중…' : '저장'}
                </button>
                <button type="button" className="btn secondary" disabled={busy}
                  onClick={() => setDraft(null)}>취소</button>
              </div>
            </div>
          )}

          {!draft && doc && (
            <div className="mn-view">
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0 }}>{doc.title}</h3>
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {[doc.part, doc.category, doc.subcategory].filter(Boolean).join(' · ')}
                    {doc.owner_name ? ` · 담당 ${doc.owner_name}` : ''}
                    {doc.updated_at ? ` · 수정 ${doc.updated_at.slice(0, 10)}` : ''}
                    {doc.updated_by_name ? ` (${doc.updated_by_name})` : ''}
                  </div>
                </div>
                {canEdit && (
                  <div className="row" style={{ gap: 6 }}>
                    <button type="button" className="btn tiny" onClick={startEdit}>편집</button>
                    <button type="button" className="btn tiny secondary" onClick={showHistory}>이력</button>
                    <button type="button" className="btn tiny secondary" onClick={archive}>내리기</button>
                  </div>
                )}
              </div>
              {!doc.published && (
                <div className="hint" style={{ marginTop: 8 }}>
                  이 문서는 <b>비공개</b>입니다. 편집 권한이 있는 사람에게만 보입니다.
                </div>
              )}
              <div className="doc-body mn-body" style={{ marginTop: 12 }}
                dangerouslySetInnerHTML={{ __html: doc.body_html || '<p class="muted">내용이 없습니다.</p>' }} />

              {history && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ margin: '0 0 6px' }}>편집 이력</h4>
                  {!history.length && <div className="muted">아직 이력이 없습니다.</div>}
                  {!!history.length && (
                    <table style={{ maxWidth: 520 }}>
                      <thead><tr><th className="l">시각</th><th className="l">고친 사람</th></tr></thead>
                      <tbody>
                        {history.map((h) => (
                          <tr key={h.id}>
                            <td className="l">{(h.saved_at || '').slice(0, 16).replace('T', ' ')}</td>
                            <td className="l">{h.saved_by_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}

          {!draft && !doc && (
            <div className="muted" style={{ padding: 24 }}>
              왼쪽에서 문서를 고르세요.
            </div>
          )}
        </div>
      </div>
    </>
  )
}
